import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { api, FirebaseContext } from "common";
import { ref, query, orderByChild, limitToLast, get, push, onValue } from "firebase/database";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { MAIN_COLOR, SECONDORY_COLOR } from "../common/sharedFunctions";
import { FirebaseConfig } from "../config/FirebaseConfig";
import moment from "moment/min/moment-with-locales";

const TIER_CONFIGS = {
  brown: {
    name: "ALPHA",
    contribution: 200,
    driverReward: 0.5,
    clientReward: 0.5,
    color: "#8B4513",
    bgColor: "#F5E6D3",
  },
  gold: {
    name: "BETA",
    contribution: 300,
    driverReward: 1.0,
    clientReward: 1.0,
    color: "#B8860B",
    bgColor: "#FFF8DC",
  },
  diamond: {
    name: "GAMMA",
    contribution: 400,
    driverReward: 1.5,
    clientReward: 1.5,
    color: "#00838F",
    bgColor: "#E0F7FA",
  },
  platinum: {
    name: "DELTA",
    contribution: 500,
    driverReward: 2.0,
    clientReward: 2.0,
    color: "#616161",
    bgColor: "#F5F5F5",
  },
};

const getTierByAmount = (amount) => {
  if (amount >= 500) return "DELTA";
  if (amount >= 400) return "GAMMA";
  if (amount >= 300) return "BETA";
  if (amount >= 200) return "ALPHA";
  return "None";
};

const getTierColor = (tierName) => {
  const entry = Object.values(TIER_CONFIGS).find((t) => t.name === tierName);
  return entry ? entry.color : "#666";
};

export default function AgentActivation() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { fetchUsersOnce, addToWallet } = api;
  const firebaseApi = useContext(FirebaseContext);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);

  const [agents, setAgents] = useState([]);
  const [activatedAgents, setActivatedAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedTierKey, setSelectedTierKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [recentActivations, setRecentActivations] = useState([]);

  // Load Credit state
  const [creditAgent, setCreditAgent] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditConfirmOpen, setCreditConfirmOpen] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
    fetchRecentActivations();
    fetchCreditHistory();
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      const agentList = staticusers.filter(
        (user) =>
          user.usertype &&
          user.usertype.includes("agent") &&
          ((user.country === auth.profile.country &&
            auth.profile.usertype === "fleetadmin") ||
            auth.profile.usertype === "admin")
      );
      setAgents(agentList);
      setActivatedAgents(agentList.filter(
        (user) => parseFloat(user.agentActivationPaid || 0) >= 200
      ));
    }
  }, [staticusers, auth.profile]);

  const fetchRecentActivations = async () => {
    try {
      const { database } = firebaseApi;
      const historyRef = query(
        ref(database, "agentActivationHistory"),
        orderByChild("timestamp"),
        limitToLast(20)
      );
      const snapshot = await get(historyRef);
      const data = snapshot.val() || {};
      const activations = Object.values(data).sort(
        (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
      );
      setRecentActivations(activations);
    } catch (error) {
      console.error("Error fetching recent activations:", error);
    }
  };

  const fetchCreditHistory = async () => {
    try {
      const { database } = firebaseApi;
      const historyRef = query(
        ref(database, "agentCreditHistory"),
        orderByChild("timestamp"),
        limitToLast(50)
      );
      const snapshot = await get(historyRef);
      const data = snapshot.val() || {};
      const history = Object.values(data).sort(
        (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
      );
      setCreditHistory(history);
    } catch (error) {
      console.error("Error fetching credit history:", error);
    }
  };

  const getAgentCurrentPaid = () => {
    if (!selectedAgent) return 0;
    return parseFloat(selectedAgent.agentActivationPaid || 0);
  };

  const getActivationAmount = () => {
    if (!selectedTierKey) return 0;
    const tierContribution = TIER_CONFIGS[selectedTierKey].contribution;
    const currentPaid = getAgentCurrentPaid();
    return Math.max(0, tierContribution - currentPaid);
  };

  const getNewTotal = () => {
    return getAgentCurrentPaid() + getActivationAmount();
  };

  const handleActivateClick = () => {
    if (!selectedAgent) {
      setSnackbar({
        open: true,
        message: t("no_agent_selected"),
        severity: "warning",
      });
      return;
    }
    if (!selectedTierKey) {
      setSnackbar({
        open: true,
        message: t("no_tier_selected"),
        severity: "warning",
      });
      return;
    }

    const activationAmount = getActivationAmount();
    if (activationAmount <= 0) {
      setSnackbar({
        open: true,
        message: t("agent_activated_already"),
        severity: "info",
      });
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmActivation = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      const activationAmount = getActivationAmount();
      const response = await fetch(
        `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/process_agent_activation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            agent_uid: selectedAgent.id,
            amount: activationAmount,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        // Log activation to history node
        const { database } = firebaseApi;
        await push(ref(database, "agentActivationHistory"), {
            agentUid: selectedAgent.id,
            agentName: `${selectedAgent.firstName || ""} ${selectedAgent.lastName || ""}`.trim(),
            agentPhone: selectedAgent.mobile || "",
            tier: TIER_CONFIGS[selectedTierKey].name,
            amount: activationAmount,
            totalPaid: result.total_paid,
            activatedBy: auth.profile.uid,
            activatedByName: `${auth.profile.firstName || ""} ${auth.profile.lastName || ""}`.trim(),
            timestamp: Date.now(),
          });

        setSnackbar({
          open: true,
          message: `${t("agent_activation_success")} ${selectedAgent.firstName} → ${TIER_CONFIGS[selectedTierKey].name} (${settings.symbol || "$"}${activationAmount})`,
          severity: "success",
        });

        // Refresh data
        dispatch(fetchUsersOnce());
        fetchRecentActivations();
        setSelectedAgent(null);
        setSelectedTierKey(null);
      } else {
        throw new Error(result.error || "Activation failed");
      }
    } catch (error) {
      console.error("Activation error:", error);
      setSnackbar({
        open: true,
        message: `${t("activation_error")} ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLoadCreditClick = () => {
    if (!creditAgent) {
      setSnackbar({ open: true, message: t("no_agent_selected"), severity: "warning" });
      return;
    }
    const parsed = parseFloat(creditAmount);
    if (!creditAmount || isNaN(parsed) || parsed <= 0) {
      setSnackbar({ open: true, message: t("valid_amount"), severity: "warning" });
      return;
    }
    setCreditConfirmOpen(true);
  };

  const handleConfirmLoadCredit = async () => {
    setCreditConfirmOpen(false);
    setCreditLoading(true);
    try {
      const parsed = parseFloat(creditAmount);
      const description = creditNote.trim()
        ? `Agent Wallet Load – ${creditNote.trim()}`
        : "Agent Wallet Load";
      const result = await dispatch(addToWallet(creditAgent.id, parsed, description));

      if (!result || !result.success) {
        throw new Error(result?.error || "Failed to credit wallet");
      }

      // Log to Firebase only on success
      const { database } = firebaseApi;
      await push(ref(database, "agentCreditHistory"), {
        agentUid: creditAgent.id,
        agentName: `${creditAgent.firstName || ""} ${creditAgent.lastName || ""}`.trim(),
        agentPhone: creditAgent.mobile || "",
        agentCountry: creditAgent.country || "",
        amount: parsed,
        note: description,
        creditedBy: auth.profile.uid,
        creditedByName: `${auth.profile.firstName || ""} ${auth.profile.lastName || ""}`.trim(),
        timestamp: Date.now(),
      });

      setSnackbar({
        open: true,
        message: `${t("credit_loaded_success") || "Credit loaded successfully"} → ${creditAgent.firstName} (+${parsed.toFixed(2)})`,
        severity: "success",
      });
      setCreditAgent(null);
      setCreditAmount("");
      setCreditNote("");
      dispatch(fetchUsersOnce());
      fetchCreditHistory();
    } catch (error) {
      console.error("Load credit error:", error);
      setSnackbar({
        open: true,
        message: `${t("activation_error")} ${error.message}`,
        severity: "error",
      });
    } finally {
      setCreditLoading(false);
    }
  };

  const activationAmount = getActivationAmount();
  const currentPaid = getAgentCurrentPaid();
  const currentTier = getTierByAmount(currentPaid);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Page Title */}
      <Typography
        variant="h4"
        sx={{ color: MAIN_COLOR, mb: 1, fontWeight: "bold" }}
      >
        {t("agent_activation_admin") || "Agent Activation"}
      </Typography>
      <Typography variant="body1" sx={{ color: "#666", mb: 3 }}>
        {t("agent_activation_admin_desc") ||
          "Activate agent accounts and manage tier assignments"}
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column: Agent Selection & Tier Selection */}
        <Grid item xs={12} md={7}>
          {/* Agent Selection */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonSearchIcon
                  sx={{ color: MAIN_COLOR, mr: 1, fontSize: 28 }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: MAIN_COLOR, fontWeight: "bold" }}
                >
                  {t("select_agent")}
                </Typography>
              </Box>

              <Autocomplete
                options={agents}
                getOptionLabel={(option) =>
                  `${option.firstName || ""} ${option.lastName || ""} (${option.mobile || "N/A"})`.trim()
                }
                value={selectedAgent}
                onChange={(_, newValue) => {
                  setSelectedAgent(newValue);
                  setSelectedTierKey(null);
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    {option.profile_image ? (
                      <Avatar
                        src={option.profile_image}
                        sx={{ width: 36, height: 36 }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ fontSize: 36, color: "#ccc" }} />
                    )}
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {option.firstName || ""} {option.lastName || ""}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#888" }}>
                        {option.mobile || "N/A"} | {option.email || "N/A"}
                      </Typography>
                    </Box>
                    <Chip
                      label={getTierByAmount(
                        parseFloat(option.agentActivationPaid || 0)
                      )}
                      size="small"
                      sx={{
                        ml: "auto",
                        backgroundColor: getTierColor(
                          getTierByAmount(
                            parseFloat(option.agentActivationPaid || 0)
                          )
                        ),
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "10px",
                      }}
                    />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("select_agent")}
                    placeholder={t("agent_select_to_activate")}
                    variant="outlined"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
              />

              {/* Selected Agent Details */}
              {selectedAgent && (
                <Card
                  sx={{
                    mt: 2,
                    backgroundColor: "#f9f9f9",
                    border: `1px solid ${MAIN_COLOR}20`,
                  }}
                >
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        {selectedAgent.profile_image ? (
                          <Avatar
                            src={selectedAgent.profile_image}
                            sx={{ width: 56, height: 56 }}
                          />
                        ) : (
                          <AccountCircleIcon
                            sx={{ fontSize: 56, color: "#ccc" }}
                          />
                        )}
                      </Grid>
                      <Grid item xs>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {selectedAgent.firstName || ""}{" "}
                          {selectedAgent.lastName || ""}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {selectedAgent.mobile || ""} |{" "}
                          {selectedAgent.email || ""}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mt: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            label={`${t("agent_current_paid")}: ${settings.symbol || "$"}${currentPaid.toFixed(2)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`${t("tier")}: ${currentTier}`}
                            size="small"
                            sx={{
                              backgroundColor: getTierColor(currentTier),
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                          {selectedAgent.country && (
                            <Chip
                              label={selectedAgent.country}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>

          {/* Tier Selection */}
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <VerifiedUserIcon
                  sx={{ color: MAIN_COLOR, mr: 1, fontSize: 28 }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: MAIN_COLOR, fontWeight: "bold" }}
                >
                  {t("select_tier")}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {Object.entries(TIER_CONFIGS).map(([key, tier]) => {
                  const isSelected = selectedTierKey === key;
                  const isBelowCurrent = tier.contribution <= currentPaid;
                  const upgradeAmt = Math.max(
                    0,
                    tier.contribution - currentPaid
                  );

                  return (
                    <Grid item xs={6} sm={3} key={key}>
                      <Card
                        sx={{
                          cursor: isBelowCurrent ? "not-allowed" : "pointer",
                          opacity: isBelowCurrent ? 0.5 : 1,
                          border: isSelected
                            ? `3px solid ${tier.color}`
                            : "1px solid #e0e0e0",
                          borderRadius: 2,
                          backgroundColor: isSelected
                            ? tier.bgColor
                            : "#fff",
                          transition: "all 0.2s ease",
                          "&:hover": isBelowCurrent
                            ? {}
                            : {
                                transform: "translateY(-2px)",
                                boxShadow: 4,
                              },
                        }}
                        onClick={() => {
                          if (!isBelowCurrent) {
                            setSelectedTierKey(key);
                          }
                        }}
                      >
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              backgroundColor: tier.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 8px",
                            }}
                          >
                            {isSelected ? (
                              <CheckCircleIcon
                                sx={{ color: "#fff", fontSize: 28 }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  color: "#fff",
                                  fontWeight: "bold",
                                  fontSize: 14,
                                }}
                              >
                                {tier.name.charAt(0)}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              color: tier.color,
                            }}
                          >
                            {tier.name}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "#333" }}
                          >
                            {settings.symbol || "$"}{tier.contribution}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#888" }}
                          >
                            {tier.driverReward + tier.clientReward}%{" "}
                            {t("daily_rewards")}
                          </Typography>
                          {selectedAgent && !isBelowCurrent && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{
                                color: MAIN_COLOR,
                                fontWeight: "bold",
                                mt: 0.5,
                              }}
                            >
                              {t("agent_upgrade_amount")}: {settings.symbol || "$"}{upgradeAmt}
                            </Typography>
                          )}
                          {isBelowCurrent && (
                            <Chip
                              label={t("activated")}
                              size="small"
                              color="success"
                              sx={{ mt: 0.5, fontSize: "10px" }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Activation Summary */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: 3,
              border: `2px solid ${MAIN_COLOR}`,
              position: "sticky",
              top: 80,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  color: MAIN_COLOR,
                  fontWeight: "bold",
                  mb: 2,
                  textAlign: "center",
                }}
              >
                {t("confirm_activation")}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {/* Summary Details */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {t("select_agent")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold" }}
                  >
                    {selectedAgent
                      ? `${selectedAgent.firstName || ""} ${selectedAgent.lastName || ""}`
                      : "—"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {t("select_tier")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color: selectedTierKey
                        ? TIER_CONFIGS[selectedTierKey].color
                        : "#999",
                    }}
                  >
                    {selectedTierKey
                      ? TIER_CONFIGS[selectedTierKey].name
                      : "—"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {t("agent_current_paid")}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {settings.symbol || "$"}{currentPaid.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {t("agent_activation_amount") || "Activation Amount"}:
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: MAIN_COLOR }}
                  >
                    {settings.symbol || "$"}{activationAmount.toFixed(2)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("agent_new_total")}:
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "green" }}
                  >
                    {settings.symbol || "$"}{getNewTotal().toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={
                  !selectedAgent ||
                  !selectedTierKey ||
                  activationAmount <= 0 ||
                  loading
                }
                onClick={handleActivateClick}
                sx={{
                  backgroundColor: MAIN_COLOR,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  borderRadius: 2,
                  "&:hover": { backgroundColor: SECONDORY_COLOR },
                }}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <VerifiedUserIcon />
                  )
                }
              >
                {loading
                  ? `${t("loading")}...`
                  : t("activate_selected_agent")}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Load Agent Credit Section */}
      <Card sx={{ mt: 3, borderRadius: 2, boxShadow: 3, border: `2px solid ${SECONDORY_COLOR}` }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AccountBalanceWalletIcon sx={{ color: SECONDORY_COLOR, mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: SECONDORY_COLOR, fontWeight: "bold" }}>
              {t("load_agent_credit") || "Load Agent Credit"}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
            {t("load_agent_credit_desc") || "Select an agent and enter the amount to credit their wallet directly."}
          </Typography>

          <Grid container spacing={3} alignItems="flex-start">
            {/* Agent Picker */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={activatedAgents}
                getOptionLabel={(option) =>
                  `${option.firstName || ""} ${option.lastName || ""} (${option.mobile || "N/A"})`.trim()
                }
                value={creditAgent}
                onChange={(_, newValue) => setCreditAgent(newValue)}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {option.profile_image ? (
                      <Avatar src={option.profile_image} sx={{ width: 32, height: 32 }} />
                    ) : (
                      <AccountCircleIcon sx={{ fontSize: 32, color: "#ccc" }} />
                    )}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.firstName || ""} {option.lastName || ""}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#888" }}>
                        {option.mobile || "N/A"} | {t("wallet_balance")}: {parseFloat(option.walletBalance || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("select_agent")}
                    placeholder={t("agent_select_to_activate")}
                    variant="outlined"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              {/* Current balance chip */}
              {creditAgent && (
                <Chip
                  icon={<AccountBalanceWalletIcon />}
                  label={`${t("current_balance")}: ${parseFloat(creditAgent.walletBalance || 0).toFixed(2)}`}
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 1.5 }}
                />
              )}
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={3}>
              <TextField
                label={t("credit_amount") || "Credit Amount"}
                placeholder="0.00"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                variant="outlined"
                fullWidth
                sx={{
                  "& label.Mui-focused": { color: SECONDORY_COLOR },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": { borderColor: SECONDORY_COLOR },
                  },
                }}
              />
            </Grid>

            {/* Optional Note */}
            <Grid item xs={12} md={3}>
              <TextField
                label={t("note") || "Note (optional)"}
                placeholder={t("credit_note_placeholder") || "e.g. Office top-up"}
                value={creditNote}
                onChange={(e) => setCreditNote(e.target.value)}
                variant="outlined"
                fullWidth
                sx={{
                  "& label.Mui-focused": { color: SECONDORY_COLOR },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": { borderColor: SECONDORY_COLOR },
                  },
                }}
              />
            </Grid>

            {/* Button */}
            <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={!creditAgent || !creditAmount || parseFloat(creditAmount) <= 0 || creditLoading}
                onClick={handleLoadCreditClick}
                sx={{
                  backgroundColor: SECONDORY_COLOR,
                  py: 1.8,
                  fontWeight: "bold",
                  borderRadius: 2,
                  "&:hover": { backgroundColor: MAIN_COLOR },
                }}
                startIcon={
                  creditLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <AccountBalanceWalletIcon />
                  )
                }
              >
                {creditLoading ? `${t("loading")}...` : (t("load_credit") || "Load Credit")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Recently Activated Agents */}
      <Card sx={{ mt: 3, borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ color: MAIN_COLOR, fontWeight: "bold", mb: 2 }}
          >
            {t("recently_activated")}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("first_name")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("mobile")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("tier")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("agent_activation_amount") || "Amount"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("total")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("signup_date")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("activated_by") || "Activated By"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentActivations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" sx={{ color: "#999" }}>
                        {t("no_data_available") || "No recent activations"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentActivations.map((activation, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{activation.agentName || "—"}</TableCell>
                      <TableCell>{activation.agentPhone || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={activation.tier}
                          size="small"
                          sx={{
                            backgroundColor: getTierColor(activation.tier),
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "10px",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {settings.symbol || "$"}{parseFloat(activation.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {settings.symbol || "$"}{parseFloat(activation.totalPaid || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {activation.timestamp
                          ? moment(activation.timestamp).format("lll")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {activation.activatedByName || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Credit Load History */}
      <Card sx={{ mt: 3, borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AccountBalanceWalletIcon sx={{ color: SECONDORY_COLOR, mr: 1, fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{ color: SECONDORY_COLOR, fontWeight: "bold" }}
            >
              {t("credit_load_history") || "Credit Load History"}
            </Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("first_name")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("mobile")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("country") || "Country"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("credit_amount") || "Amount"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("note") || "Note"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("credited_by") || "Credited By"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("signup_date")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" sx={{ color: "#999" }}>
                        {t("no_data_available") || "No credit history yet"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  creditHistory.map((entry, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{entry.agentName || "—"}</TableCell>
                      <TableCell>{entry.agentPhone || "—"}</TableCell>
                      <TableCell>{entry.agentCountry || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={parseFloat(entry.amount || 0).toFixed(2)}
                          size="small"
                          sx={{
                            backgroundColor: "#4CAF50",
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "11px",
                          }}
                        />
                      </TableCell>
                      <TableCell>{entry.note || "—"}</TableCell>
                      <TableCell>{entry.creditedByName || "—"}</TableCell>
                      <TableCell>
                        {entry.timestamp
                          ? moment(entry.timestamp).format("lll")
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Load Credit Confirmation Dialog */}
      <Dialog open={creditConfirmOpen} onClose={() => setCreditConfirmOpen(false)}>
        <DialogTitle sx={{ color: SECONDORY_COLOR, fontWeight: "bold" }}>
          {t("confirm_load_credit") || "Confirm Credit Load"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("load_credit_confirm_desc") || "Please confirm the following credit transaction:"}
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{t("select_agent")}:</strong>{" "}
              {creditAgent ? `${creditAgent.firstName || ""} ${creditAgent.lastName || ""} (${creditAgent.mobile || ""})` : ""}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>{t("credit_amount") || "Credit Amount"}:</strong>{" "}
              {parseFloat(creditAmount || 0).toFixed(2)}
            </Typography>
            {creditNote.trim() && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>{t("note") || "Note"}:</strong> {creditNote}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 0.5, color: "green", fontWeight: "bold" }}>
              <strong>{t("new_balance") || "New Balance"}:</strong>{" "}
              {(parseFloat(creditAgent?.walletBalance || 0) + parseFloat(creditAmount || 0)).toFixed(2)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditConfirmOpen(false)} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmLoadCredit}
            variant="contained"
            sx={{ backgroundColor: SECONDORY_COLOR, "&:hover": { backgroundColor: MAIN_COLOR } }}
            autoFocus
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="activation-confirm-dialog"
      >
        <DialogTitle
          id="activation-confirm-dialog"
          sx={{ color: MAIN_COLOR, fontWeight: "bold" }}
        >
          {t("confirm_activation")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("agent_activation_confirm")}
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{t("select_agent")}:</strong>{" "}
              {selectedAgent
                ? `${selectedAgent.firstName || ""} ${selectedAgent.lastName || ""} (${selectedAgent.mobile || ""})`
                : ""}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>{t("select_tier")}:</strong>{" "}
              {selectedTierKey ? TIER_CONFIGS[selectedTierKey].name : ""}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>{t("agent_activation_amount")}:</strong>{" "}
              {settings.symbol || "$"}{activationAmount.toFixed(2)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, fontWeight: "bold", color: "green" }}
            >
              <strong>{t("agent_new_total")}:</strong>{" "}
              {settings.symbol || "$"}{getNewTotal().toFixed(2)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmActivation}
            variant="contained"
            sx={{
              backgroundColor: MAIN_COLOR,
              "&:hover": { backgroundColor: SECONDORY_COLOR },
            }}
            autoFocus
          >
            {t("activate_selected_agent")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
